import React from 'react';
import { Helmet } from 'react-helmet';

const slugifyCity = (value = '') => {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-');
};

const SEOHead = ({ housing, images }) => {
  if (!housing) return null;

  const title = `${housing.title} - Alojamiento en ${housing.location?.location || 'Argentina'} | Alohar`;
  const locationName = housing.location?.location || 'Argentina';
  const normalizedDescription = housing.description
    ? housing.description.replace(/\s+/g, ' ').trim()
    : '';
  const description = normalizedDescription
    ? `${normalizedDescription.substring(0, 160)}${normalizedDescription.length > 160 ? '...' : ''}`
    : `Alojamiento ${housing.typeHousingProperty} para ${housing.guests} huéspedes en ${housing.location?.location || 'Argentina'}. Reserva ahora en Alohar.`;
  
  const imageUrl = images?.[0]?.media || `${process.env.REACT_APP_BASE_URL || 'https://alohar.me'}/og.png`;
  const citySlug = slugifyCity(locationName);
  const currentUrl = citySlug
    ? `https://alohar.me/${citySlug}/property/${housing.id}`
    : `https://alohar.me/property/${housing.id}`;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LodgingBusiness",
    "name": housing.title,
    "description": description,
    "image": imageUrl,
    "url": currentUrl,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": housing.location?.location,
      "addressCountry": "AR"
    },
    "geo": housing.lat && housing.lng ? {
      "@type": "GeoCoordinates",
      "latitude": housing.lat,
      "longitude": housing.lng
    } : undefined,
    "amenityFeature": housing.housingService?.map(service => ({
      "@type": "LocationFeatureSpecification",
      "name": service
    })),
    "numberOfRooms": housing.rooms,
    "occupancy": {
      "@type": "QuantitativeValue",
      "maxValue": housing.guests
    },
    "priceRange": housing.basePrice ? `$${housing.basePrice}` : undefined
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={currentUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Alohar" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@alohar" />

      {/* Additional SEO */}
      <meta name="keywords" content={`alojamiento, ${housing.typeHousingProperty}, ${housing.location?.location}, hospedaje, vacaciones`} />
      <meta name="author" content="Alohar" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData, null, 2)}
      </script>
    </Helmet>
  );
};

export default SEOHead;